import { t, useLanguage } from "@/i18n/LanguageContext";import { useState } from "react";
import { Lock, LogOut, AlertTriangle, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { LOGO_URL } from "@/content/defaults";
import { useSite } from "@/context/SiteContext";
import ContentTab from "@/pages/admin/ContentTab";
import ProjectsTab from "@/pages/admin/ProjectsTab";
import MessagesTab from "@/pages/admin/MessagesTab";
import ChatsTab from "@/pages/admin/ChatsTab";
import InquiriesTab from "@/pages/admin/InquiriesTab";
import AnalyticsTab from "@/pages/admin/AnalyticsTab";
import { useSearchParams } from "react-router-dom";

const TABS = [
{ key: "contenuti", label: "Contenuti sito", testid: "admin-tab-contenuti" },
{ key: "lavori", label: "I nostri lavori", testid: "admin-tab-lavori" },
{ key: "messaggi", label: "Messaggi ricevuti", testid: "admin-tab-messaggi" },
{ key: "richieste", label: "Preventivi e CV", testid: "admin-tab-richieste" },
{ key: "chat", label: "Chat visitatori", testid: "admin-tab-chat" },
{ key: "statistiche", label: "Statistiche", testid: "admin-tab-statistiche" }];


const WARN_DAYS = 180;

function checkExpiries(certifications) {
  const now = new Date();
  const limit = new Date(now.getTime() + WARN_DAYS * 86400000);
  const expired = [];
  const expiring = [];
  for (const c of certifications) {
    if (!c.expiry) continue;
    const [d, m, y] = c.expiry.split("/").map(Number);
    if (!d || !m || !y) continue;
    const dt = new Date(y, m - 1, d);
    if (dt < now) expired.push(c);else
    if (dt <= limit) expiring.push(c);
  }
  return { expired, expiring };
}

export default function Admin() {useLanguage();
  const { content } = useSite();
  const [token, setToken] = useState(() => localStorage.getItem("gfc_token"));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = TABS.some((item) => item.key === searchParams.get("tab")) ? searchParams.get("tab") : "contenuti";
  const setTab = (key) => setSearchParams({ tab: key });

  const logout = () => {
    localStorage.removeItem("gfc_token");
    setToken(null);
  };

  const onAuthError = () => {
    localStorage.removeItem("gfc_token");
    setToken(null);
    toast.error(t("Sessione scaduta. Accedi di nuovo."));
  };

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("gfc_token", data.token);
      setToken(data.token);
      toast.success(t("Accesso effettuato"));
    } catch (err) {
      setError(formatApiError(err, "Errore di accesso"));
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-brand-navydeep px-4" data-testid="admin-login-page">
        <form onSubmit={login} className="w-full max-w-md rounded-2xl bg-white p-8 shadow-2xl sm:p-10" data-testid="admin-login-form">
          <div className="mb-8 flex flex-col items-center">
            <img src={LOGO_URL} alt={t("Logo GFC")} className="h-16 w-16 rounded-full object-cover" />
            <h1 className="mt-4 font-display text-xl font-bold text-brand-navydeep">{t("Area riservata")}</h1>
            <p className="mt-1 text-sm text-slate-500">{t("Gestione contenuti del sito GFC")}</p>
          </div>
          <label htmlFor="admin-email" className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-slate-600">{t("Email")}</label>
          <input id="admin-email" type="email" data-testid="admin-email-input" required value={email} onChange={(e) => setEmail(e.target.value)}
          className="mb-4 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-brand-navy focus:ring-2 focus:ring-brand-navy/10" />
          <label htmlFor="admin-password" className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-slate-600">{t("Password")}</label>
          <input id="admin-password" type="password" data-testid="admin-password-input" required value={password} onChange={(e) => setPassword(e.target.value)}
          className="mb-6 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-brand-navy focus:ring-2 focus:ring-brand-navy/10" />
          {t(error && <p data-testid="admin-login-error" className="mb-4 rounded-lg bg-red-50 px-4 py-2.5 text-sm font-medium text-red-600">{t(error)}</p>)}
          <button type="submit" data-testid="admin-login-button" disabled={loading}
          className="flex w-full items-center justify-center gap-2 rounded-full bg-brand-navy py-3.5 font-display text-sm font-bold text-white transition-colors hover:bg-brand-navydark disabled:opacity-60">
            <Lock className="h-4 w-4" /> {t(loading ? "Accesso…" : "Accedi")}
          </button>
        </form>
      </main>);

  }

  const { expired, expiring } = checkExpiries(content.certifications || []);

  return (
    <main className="min-h-screen bg-slate-100" data-testid="admin-dashboard">
      <div className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt={t("Logo GFC")} className="h-9 w-9 rounded-full object-cover" />
            <div>
              <h1 className="font-display text-lg font-bold text-brand-navydeep">{t("Pannello di gestione")}</h1>
              <p className="text-xs text-slate-500">{t("GFC Appalti Generali S.r.l.")}</p>
            </div>
          </div>
          <button onClick={logout} data-testid="admin-logout-button"
          className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 transition-colors hover:border-red-300 hover:text-red-600">
            <LogOut className="h-4 w-4" />{t(" Esci")}
          </button>
        </div>
        <div className="mx-auto flex max-w-7xl gap-1 overflow-x-auto px-4 sm:px-6 lg:px-8">
          {TABS.map((tab_) =>
          <button key={tab_.key} data-testid={tab_.testid} onClick={() => setTab(tab_.key)}
          className={`whitespace-nowrap border-b-2 px-4 py-3 text-sm font-bold transition-colors ${
          tab === tab_.key ? "border-brand-lime text-brand-navy" : "border-transparent text-slate-500 hover:text-brand-navy"}`
          }>
              {t(tab_.label)}
            </button>
          )}
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        {t(expired.length > 0 &&
        <div data-testid="cert-expired-banner" className="mb-6 flex items-start gap-3 rounded-2xl border border-red-300 bg-red-50 px-6 py-4">
            <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0 text-red-600" />
            <p className="text-sm text-red-700">
              <strong>{t("Certificazioni scadute:")}</strong>{t(" ")}
              {t(expired.map((c) => `${c.code} (${c.expiry})`).join(", "))}{t(" — aggiorna i documenti nella scheda Contenuti sito.")}
          </p>
          </div>)
        }
        {t(expiring.length > 0 &&
        <div data-testid="cert-expiring-banner" className="mb-6 flex items-start gap-3 rounded-2xl border border-amber-300 bg-amber-50 px-6 py-4">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
            <p className="text-sm text-amber-800">
              <strong>{t("In scadenza entro ")}{t(WARN_DAYS)}{t(" giorni:")}</strong>{t(" ")}
              {t(expiring.map((c) => `${c.code} (${c.expiry})`).join(", "))}{t(" — pianifica il rinnovo.")}
          </p>
          </div>)
        }
        {t(tab === "contenuti" && <ContentTab onAuthError={onAuthError} />)}
        {t(tab === "lavori" && <ProjectsTab onAuthError={onAuthError} />)}
        {t(tab === "messaggi" && <MessagesTab onAuthError={onAuthError} />)}
        {t(tab === "richieste" && <InquiriesTab onAuthError={onAuthError} />)}
        {t(tab === "chat" && <ChatsTab onAuthError={onAuthError} />)}
        {tab === "statistiche" && <AnalyticsTab onAuthError={onAuthError} />}
      </div>
    </main>);

}
